// apps/workflow/src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import pb from '../lib/pocketbaseClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user from PocketBase auth store
  const loadUser = useCallback(() => {
    if (pb.authStore.isValid && pb.authStore.model) {
      setUser(pb.authStore.model);
    } else {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    loadUser();
    setLoading(false);

    // Listen for auth state changes (login/logout)
    const unsubscribe = pb.authStore.onChange(() => {
      loadUser();
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [loadUser]);

  // Login function with error handling
  const login = async (email, password) => {
    try {
      const authData = await pb.collection('users').authWithPassword(email, password);
      setUser(authData.record);
      return { success: true, user: authData.record };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  // Logout
  const logout = () => {
    pb.authStore.clear();
    setUser(null);
  };

  // Refresh user data from server (useful after profile updates)
  const refreshUser = async () => {
    if (!pb.authStore.isValid || !pb.authStore.model) {
      setUser(null);
      return null;
    }
    try {
      const refreshed = await pb.collection('users').getOne(pb.authStore.model.id);
      setUser(refreshed);
      return refreshed;
    } catch (error) {
      console.error('Refresh user error:', error);
      pb.authStore.clear();
      setUser(null);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    role: user?.role || null, // 🔑 role is now easily accessible
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;